import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Dialog, FlatButton, FontIcon, RaisedButton, SelectField, MenuItem, CircularProgress, TextField } from 'material-ui';
import { RadioButton, RadioButtonGroup } from 'material-ui/RadioButton';
import styles from 'styles/TBenefits';
import { filter, get, isNaN, map, mapValues, toString, toNumber, isNumber, find } from 'lodash';
import { connect, useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { calculate, calculateReset, createPerDiemApprovalRequest, initApproval, usePerDiemState } from '../../actions/PerDiem';
import KTitle from '../../components/KJRN/KTitle';
import { PerDiemAmountTypeEnum } from '../../constants/ActionTypes';
import PerDiem from '../../images/PerDiem';
import pallet from '../../constants/colors';

const labelStyle = {
  color: 'rgba(0, 0, 0, 0.3)',
  fontSize: '12px',
  marginTop: '16px',
};

const errorStyle = {
  color: 'red',
  fontSize: '12px',
  margintop: 20,
};

const tableStyle = {
  display: 'flex',
  border: '1px solid',
};

const rowStyle = {
  border: '1px solid',
  paddingLeft: '4px',
  paddingRight: '4px'
};

const labelColumn = {
  backgroundColor: '#ddd',
  width: '50%',
};

const isNumeric = value => !Number.isNaN(value - parseFloat(value));

const unitRender = (n, digits) => {
  if (typeof digits !== 'number') digits = 2; // eslint-disable-line no-param-reassign
  if (isNumeric(n)) n = toNumber(n); // eslint-disable-line no-param-reassign
  return !isNumber(n)
    ? n
    : n.toLocaleString('de-DE', { maximumFractionDigits: digits, minimumFractionDigits: digits });
};

const currencyRender = n => (n ? n.toLocaleString(
  // Need to use German because Icelandic is not working
  'de-DE',
  { maximumFractionDigits: 0, minimumFractionDigits: 0 },
) : n);

const TPerDiem = ({ DefaultEmployeeDetailID }) => {
  const dispatch = useDispatch();
  const { WebValuesReducer } = useSelector(state => state);
  const { valuesByKey } = WebValuesReducer;

  useEffect(() => { dispatch(initApproval()); }, [dispatch]);

  const {
    perDiemList,
    perDiemLocationList,
    perDiemTypeList,
    perDiemListKeyed,
    perDiemLocationListKeyed,
    perDiemAmountTypeFixed,
    calculating,
    calculatedRecord,
  } = usePerDiemState();

  const formInitialValues = {
    PeriodBegda: '',
    PeriodEndda: '',
    Duration: 0,
    Location: 0,
    PerDiemTypeID: 0,
    Info: '',
    ConnectedID: 0
  };
  const [perDiems, setPerDiems] = useState(perDiemList);
  const [selectedPerDiem, setSelectedPerDiem] = useState();
  const [errors, setErrors] = useState({});
  const [dateError, setDateError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(formInitialValues);
  // handle comment here because otherwise the modal rerenders on every keypress
  const [comment, setComment] = useState('');
  const openDialog = () => setShowDialog(true);
  const closeDialog = () => {
    setForm(formInitialValues);
    setSelectedPerDiem(null);
    setShowDialog(false);
  };

  const defaultType = perDiemAmountTypeFixed || get(valuesByKey, ['PerDiem.Default.Type', 'Value'], 'Total');

  const commentRequired = get(valuesByKey, ['PerDiem.Comment.Required', 'Value'], 'false') === 'true';
  const getRadioLabel = amountType => (
    <div style={{ display: 'flex', flexDirection: 'row' }}>
      <FormattedMessage id={`PerDiem.PerDiemAmountTypeEnum.${amountType}`} />
      {selectedPerDiem ? (
        <div>
          {' - '}
          {currencyRender(get(selectedPerDiem, `${amountType}Amount`, ''))}
          {' '}
          {get(selectedPerDiem, 'Currency', '')}
        </div>
      ) : null}
    </div>
  );

  const shouldCalculate = () => {
    if (calculating) return;
    // missing DaysOverride days
    const days = get(form, 'Duration') ? Number(get(form, 'Duration')) : 0;
    const daysOverride = days !== 0 ? days : undefined;
    if (get(form, 'ConnectedID') && get(form, 'Location') && get(form, 'Info') && get(form, 'PeriodBegda') && get(form, 'PeriodEndda') && DefaultEmployeeDetailID) {
      dispatch(calculate({
        EmployeeDetailID: DefaultEmployeeDetailID,
        PerDiemID: get(form, 'ConnectedID'),
        DaysOverride: daysOverride,
        PerDiemLocationID: get(form, 'Location'),
        PerDiemAmountType: get(form, 'Info'),
        Begda: moment(get(form, 'PeriodBegda')).startOf('day').toISOString(),
        Endda: moment(get(form, 'PeriodEndda')).set({ hour: '23', minutes: '59', seconds: '59' }).toISOString(),
      }));
    } else {
      dispatch(calculateReset());
    }
  };

  useEffect(() => {
    shouldCalculate();
  }, [form, perDiems]);
  const checkIfDateError = (PeriodBegda, PeriodEndda) => {
    const from = moment(moment(PeriodBegda).format('YYYY-MM-DD'));
    const to = moment(moment(PeriodEndda).format('YYYY-MM-DD'));
    const isPeriodBegdaBeforePeriodEndda = to.isBefore(from);
    setDateError(isPeriodBegdaBeforePeriodEndda);
  };

  const getHours = (PeriodBegda, PeriodEndda) => {
    if (PeriodBegda && PeriodEndda) {
      const timeFrom = moment(PeriodBegda);
      const timeTo = moment(PeriodEndda);
      let Days = timeTo.diff(timeFrom, 'Days') + 1;

      if (isNaN(Days)) { Days = 0; }
      return Days;
    }
    return 0;
  };

  const radioIsDisabled = (amountType) => {
    // If perDiemAmountTypeFixed from webValues is used, we only allow that type
    const isDisabled = perDiemAmountTypeFixed && amountType !== perDiemAmountTypeFixed;
    // Disable the radio if the amount is zero, no reason to allow that.
    const amount = get(selectedPerDiem, `${amountType}Amount`, 0);
    if (amount === 0) return true;
    // If the perdiem is domestic we only allow selecting total
    const domestic = get(selectedPerDiem, 'PerDiemDomesticInternational');
    return (domestic === 'PerDiemDomestic' && amountType !== 'Total') || isDisabled;
  };


  const formChanged = (key, value) => {

    if (key === 'Location') {
      const type = get(perDiemLocationListKeyed[value], 'PerDiemTypeID');
      const filteredPerDiems = filter(perDiemList, ['PerDiemTypeID', type]);
      // if there is only one PerDiem to choose from, we use that as default.
      const connectId = filteredPerDiems.length === 1 ? get(filteredPerDiems[0], 'PerDiemID') : undefined;
      if (connectId) {
        setSelectedPerDiem(perDiemListKeyed[connectId]);
      }
      setPerDiems(filteredPerDiems);
      setForm({
        ...form,
        Location: value,
        PerDiemTypeID: type,
        ConnectedID: connectId,
        Info: connectId ? defaultType : '',
      });
    }
    if (key === 'ConnectedID') {
      setSelectedPerDiem(perDiemListKeyed[value]);
      setForm({ ...form, ConnectedID: value, Info: defaultType });
    }

    if (key === 'PeriodEndda' || key === 'PeriodBegda') {
      let days = 0;
      if (get(form, 'PeriodBegda') !== '' && key === 'PeriodEndda') {
        days = getHours(get(form, 'PeriodBegda'), value);
        checkIfDateError(get(form, 'PeriodBegda'), value, key);
      } else if (get(form, 'PeriodEndda') !== '' && key === 'PeriodBegda') {
        days = getHours(value, get(form, 'PeriodEndda'));
        checkIfDateError(value, get(form, 'PeriodEndda'), key);
      }
      setForm({ ...form, Duration: days, [key]: value });
    }
    if (key === 'Info') {
      setForm({ ...form, [key]: value });
    }

    setErrors({ ...errors, [key]: !value });
  };
  const areErrors = () => {
    const commentError = commentRequired && !comment;
    setErrors({
      ...mapValues(form, (val, key) => key !== 'PerDiemTypeID' && !val),
      RequestDescription: commentError,
    });
    if (!get(form, 'PeriodBegda') || !get(form, 'PeriodEndda') || !get(form, 'Duration') || !get(form, 'ConnectedID') || !get(form, 'Location') || !get(form, 'Info') || commentError) return true;
    return false;
  };
  const reset = () => {
    setComment('');
    setSelectedPerDiem(null);
    setForm({});
    setLoading(false);
    closeDialog();
  };

  const submit = () => {
    if (!areErrors()) {
      setLoading(true);

      const formObject = form;
      delete formObject.PerDiemTypeID;
      const Begda = moment(formObject.PeriodBegda).startOf('day').toISOString();
      const Endda = moment(formObject.PeriodEndda).set({ hour: '23', minutes: '59', seconds: '59' }).toISOString();
      const Location = toString(formObject.Location);
      const data = {
        ...formObject, PeriodBegda: Begda, PeriodEndda: Endda, Location, RequestDescription: comment,
      };

      dispatch(createPerDiemApprovalRequest(data, reset));
    }
  };

  return (
    <div style={styles.container}>
      <Dialog
        contentStyle={{
          overflow: 'hidden',
          maxWidth: 500,
          width: '100%',
        }}
        bodyStyle={{
          overflowY: 'auto',
        }}
        modal
        open={showDialog}
        onRequestClose={closeDialog}
      >
        <KTitle
          style={{
            marginLeft: 0,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
          }}
          title={(
            <React.Fragment>
              <FontIcon
                style={{
                  marginRight: 6, fontSize: 28, color: 'inherit',
                }}
                className="material-icons"
              >money
              </FontIcon>
              <FormattedMessage id="dash.title.perDiem" />
            </React.Fragment>
          )}
          subtitleStyle={{ margin: 0, fontSize: 12, lineHeight: '1' }}
          subtitle={(
            <React.Fragment>
              <FormattedMessage id="PerDiem.Description" />
            </React.Fragment>
          )}
        />
        <div style={{
          display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'
        }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <TextField
              id="PeriodBegda"
              type="date"
              floatingLabelFixed
              floatingLabelText={<FormattedMessage id="time.DateIn" />}
              errorText={errors.PeriodBegda ? <FormattedMessage id="editPanel.required" /> : ''}
              value={get(form, 'PeriodBegda')}
              onChange={(event) => { formChanged('PeriodBegda', event.target.value); }}
            />

            <TextField
              id="PeriodEndda"
              type="date"
              floatingLabelFixed
              floatingLabelText={<FormattedMessage id="time.DateOut" />}
              errorText={errors.PeriodBegda ? <FormattedMessage id="editPanel.required" /> : ''}
              value={get(form, 'PeriodEndda')}
              onChange={(event) => { formChanged('PeriodEndda', event.target.value); }}
            />

            {dateError && <div style={errorStyle}><FormattedMessage id="time.error.DateOutBeforeDateIn" /></div>}
          </div>

          {(get(form, 'PeriodBegda') !== '' && get(form, 'PeriodEndda') !== '' && dateError !== true) &&
            <div style={{ marginRight: 50 }}>
              {getHours(get(form, 'PeriodBegda'), get(form, 'PeriodEndda'))} <FormattedMessage id="PerDiem.Days" />
            </div>
          }
        </div>

        <SelectField
          fullWidth
          floatingLabelFixed
          floatingLabelText={<FormattedMessage id="PerDiem.PerDiemLocationID" />}
          value={get(form, 'Location')}
          onChange={(event, index, value) => { formChanged('Location', value); }}
        >
          {map(perDiemLocationList, ({ PerDiemLocationID, PerDiemLocationName }) => (
            <MenuItem
              key={PerDiemLocationID}
              value={PerDiemLocationID}
              label={PerDiemLocationName}
              primaryText={PerDiemLocationName}
            />
          ))}
        </SelectField>
        {errors.Location && <div style={errorStyle}><FormattedMessage id="editPanel.required" /></div>}
        <TextField
          id="PerDiemType"
          type="text"
          fullWidth
          floatingLabelFixed
          floatingLabelText={<FormattedMessage id="PerDiem.PerDiemTypeID" />}
          disabled
          defaultValue={get(find(perDiemTypeList, p => get(form, 'PerDiemTypeID') === p.PerDiemTypeID), 'PerDiemTypeName') || ''}
        />
        {errors.PerDiemTypeID && <div style={errorStyle}><FormattedMessage id="editPanel.required" /></div>}

        <SelectField
          fullWidth
          floatingLabelFixed
          floatingLabelText={<FormattedMessage id="PerDiem" />}
          value={get(form, 'ConnectedID')}
          onChange={(event, index, value) => { formChanged('ConnectedID', value); }}
        >
          {map(perDiems, ({ PerDiemID, PerDiemDescription }) => (
            <MenuItem
              key={PerDiemID}
              value={PerDiemID}
              label={PerDiemDescription}
              primaryText={PerDiemDescription}
            />
          ))}
        </SelectField>
        {errors.ConnectedID && <div style={errorStyle}><FormattedMessage id="editPanel.required" /></div>}

        <div style={labelStyle}>
          <FormattedMessage id="PerDiem.PerDiemAmountType" />
        </div>
        <RadioButtonGroup name="PerDiemAmountType" valueSelected={get(form, 'Info')} onChange={(_, val) => formChanged('Info', val)}>
          {map(PerDiemAmountTypeEnum, amountType => (
            <RadioButton
              disabled={radioIsDisabled(amountType)}
              style={{ display: 'block' }}
              key={amountType}
              value={amountType}
              label={getRadioLabel(amountType)}
            />
          ))}
        </RadioButtonGroup>

        {calculatedRecord ? (
          <div>
            {calculating ? (<CircularProgress style={{ marginRight: 16, lineHeight: '24px' }} size={24} thickness={2} />) :
              // eslint-disable-next-line react/jsx-indent
              <div>
                <div style={{ marginTop: '20px' }}>
                  <FormattedMessage id="PerDiemCalculated" />
                </div>
                <div style={tableStyle}>
                  <div style={labelColumn}>
                    {get(calculatedRecord, 'Currency') !== 'ISK' ? (<div style={rowStyle}><FormattedMessage id="PerDiemCalculated.ExchangeRate" values={{ currency: get(calculatedRecord, 'Currency') }} /></div>) : null}
                    <div style={rowStyle}><FormattedMessage id="PerDiemCalculated.Days" /></div>
                    <div style={rowStyle}><FormattedMessage id="PerDiemCalculated.TotalAmountCurrency" values={{ currency: get(calculatedRecord, 'Currency') }} /></div>
                    <div style={rowStyle}><FormattedMessage id="PerDiemCalculated.TotalAmountISK" /></div>
                    <div style={rowStyle}><FormattedMessage id="PerDiemCalculated.EstimatedTaxISK" /></div>
                    {get(calculatedRecord, 'PaymentCurrency', 'None') === 'None' ? null : (
                      <div style={rowStyle}><FormattedMessage id="PerDiemCalculated.TotalAmountPrepaidPaymentCurrency" values={{ currency: get(calculatedRecord, 'PaymentCurrency') }} /></div>)}
                  </div>
                  <div style={{ width: '50%' }}>
                    {get(calculatedRecord, 'Currency') !== 'ISK' ? (<div style={rowStyle}>{unitRender(get(calculatedRecord, 'ExchangeRate'))}</div>) : null}
                    <div style={rowStyle}>{get(calculatedRecord, 'DaysOverride') || get(calculatedRecord, 'Days')} </div>
                    <div style={rowStyle}>{unitRender(get(calculatedRecord, 'TotalAmountCurrency'))}</div>
                    <div style={rowStyle}><FormattedMessage id="ISK" values={{ amount: currencyRender(get(calculatedRecord, 'TotalAmountISK')) }} /></div>
                    <div style={rowStyle}><FormattedMessage id="ISK" values={{ amount: currencyRender(get(calculatedRecord, 'EstimatedTaxISK')) }} /></div>
                    {get(calculatedRecord, 'PaymentCurrency', 'None') === 'None' ? null : (<div style={rowStyle}>{unitRender(get(calculatedRecord, 'TotalAmountPrepaidISK') / get(calculatedRecord, 'PaymentExchangeRate'))}</div>)}
                  </div>
                </div>
              </div>
            }
          </div>
        ) : null}
        <TextField
          id="RequestDescription"
          type="text"
          multiLine
          fullWidth
          floatingLabelFixed
          floatingLabelText={<FormattedMessage id={commentRequired ? 'PerDiemComment.Custom' : 'comment'} />}
          errorText={errors.RequestDescription && commentRequired ? <FormattedMessage id="editPanel.required" /> : ''}
          onChange={event => setComment(event.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <FlatButton
            label={<FormattedMessage id="time.cancel" />}
            secondary
            onClick={() => closeDialog()}
            disabled={loading}
          />
          <RaisedButton
            type="submit"
            label={<FormattedMessage id="time.create" />}
            primary
            onClick={submit}
            disabled={loading || dateError}
            loading={`${loading}`}
          />
        </div>
      </Dialog>
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* <Travel /> */}
        <PerDiem />
        <div
          style={{
            padding: 8,
            marginBottom: 12,
            marginTop: 12,
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <FontIcon style={{ marginRight: 8 }} color={pallet.infoColor} className="material-icons">info</FontIcon>
          <FormattedMessage id="PerDiem.Description" />
        </div>
        <RaisedButton
          primary
          style={{ marginBottom: 12 }}
          onClick={openDialog}
          label={<FormattedMessage id="PerDiem.Button" />}
        />
      </div>
    </div>
  );
};

TPerDiem.propTypes = {
  DefaultEmployeeDetailID: PropTypes.number.isRequired
};

function mapStateToProps(state) {
  return {
    DefaultEmployeeDetailID: state.loginReducer.user.DefaultEmployeeDetailID,
  };
}

export default connect(mapStateToProps)(TPerDiem);



// WEBPACK FOOTER //
// ./src/containers/tiles/TPerDiem.js